<template>
  <div class="user-guide-page">
    <header class="guide-header">
      <v-btn icon class="back-btn" @click="goBack">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <h1 class="guide-title">User Guide</h1>
    </header>

    <div class="guide-content">
      <section id="toc" class="guide-section guide-toc">
        <h2>Table of contents</h2>
        <nav class="toc-nav">
          <a href="#what-is-rankster">What is Project Rankster?</a>
          <a href="#crowd-sourced">Crowd-sourced data</a>
          <a href="#how-rankings-work">How the rankings work</a>
          <a href="#home-screen">Home screen</a>
          <a href="#states-leagues-divisions">States, leagues, and divisions</a>
          <a href="#wrestler-profile">Wrestler profile</a>
          <a href="#match-detail">Match detail screen</a>
          <a href="#adding-match">Adding a match</a>
          <a href="#adding-editing-wrestlers">Adding and editing wrestlers</a>
          <a href="#leagues-rankings">Viewing league rankings</a>
          <a href="#login-signup">Login and sign up</a>
          <a href="#dark-mode">Dark mode</a>
          <a href="#admin-tools">League management &amp; admin (operators)</a>
          <a href="#tips">Tips for better data</a>
        </nav>
      </section>

      <section id="what-is-rankster" class="guide-section">
        <h2>What is Project Rankster?</h2>
        <p>
          Project Rankster is an
          <strong>information-based wrestling rankings</strong> web app. It
          helps athletes, coaches, and fans see how wrestlers compare within a
          league and weight class using an ELO-style skill rating that updates
          with every match result.
        </p>
        <p>
          You can <strong>browse</strong> wrestlers, matches, and rankings
          without an account. To <strong>add or edit</strong> wrestlers and
          matches, you need to create an account and log in.
        </p>
      </section>

      <section id="crowd-sourced" class="guide-section highlight">
        <h2>Crowd-sourced data</h2>
        <p>
          <strong>All data in the app is crowd-sourced.</strong> Wrestlers,
          matches, leagues, and weight classes are entered and maintained by
          users. The rankings are driven entirely by what the community enters.
        </p>
        <p>
          <strong>The more users who enter high-quality data</strong> (correct
          wrestlers, complete match results, accurate dates and match types),
          <strong>the better the rankings.</strong> Good data leads to more
          accurate skill ratings for everyone.
        </p>
      </section>

      <section id="how-rankings-work" class="guide-section">
        <h2>How the rankings work</h2>
        <p>
          Each wrestler has a <strong>Skill</strong> rating (similar to ELO). It
          starts at 1200 and goes up or down after each match based on the
          opponent’s skill, the result (win/loss), and match type (Decision,
          Major, Technical Fall, Fall, etc.). Wins against stronger opponents
          increase skill more; losses decrease it.
        </p>
        <p>
          <strong>Rankings</strong> within a league and weight class are ordered
          by <strong>Skill</strong> (highest first). When two wrestlers are
          within <strong>20 skill points</strong> of each other, they are
          treated as effectively tied: whoever won the
          <strong>most recent head-to-head</strong> match between them (among
          matches stored in the app) is ranked ahead. Pound-for-pound uses the
          same rule within the division. Rank-change badges (e.g. ↑2 or ↓1) show
          movement since the last time rankings were saved.
        </p>
      </section>

      <section id="home-screen" class="guide-section">
        <h2>Home screen</h2>
        <p>The home screen has three main areas:</p>
        <ul>
          <li>
            <strong>Discover wrestlers</strong> — Search by name or school. You
            can filter by league, weight, minimum skill, and maximum skill.
            Tap a wrestler to open their profile.
          </li>
          <li>
            <strong>Leagues</strong> — Divisions are grouped by
            <strong>State</strong> and <strong>League</strong> (e.g. Illinois →
            IESA → IESA A, IESA AA). Each division card shows the division name
            and a default wrestling icon if no logo is set.
            <strong>Tap anywhere on the card</strong> to open that division’s
            rankings page. You’ll see all leagues for a state on one line when
            the screen is wide enough.
          </li>
          <li>
            <strong>Match feed</strong> — A scrollable feed of recent matches
            (newest first). Each card shows the two wrestlers’ profile pictures:
            the <strong>winner</strong> has a <strong>green outline</strong> and
            a <strong>W</strong> badge; the <strong>loser</strong> has a
            <strong>red outline</strong> and an <strong>L</strong> badge. Hover
            over a picture to see that wrestler’s name. The result line always
            shows <strong>Winner name · Win by [type] · Loser name</strong>. Tap
            a match to open the match detail; tap a wrestler’s picture to open
            their profile.
          </li>
        </ul>
        <p>
          The <strong>Season</strong> menu in the top red toolbar lists only
          seasons that have at least one match in the database, plus
          <strong>All</strong>. It filters the <strong>match feed</strong> (and
          league / weight rankings: only wrestlers with a match in that season).
          The selection is saved on this device and stays in place as you move
          between home and rankings.
        </p>
        <p>
          The <strong>floating menu</strong> (bottom-right) includes:
          <strong>Home</strong>, <strong>Log in</strong> /
          <strong>Log out</strong>, this <strong>User Guide</strong>,
          <strong>Back</strong>, <strong>Dark mode</strong> /
          <strong>Light mode</strong> (toggle), and when you are logged in:
          <strong>Add wrestler</strong> and <strong>Add match</strong>. For a
          small set of designated operator accounts, the menu also shows
          <strong>Manage leagues</strong> and
          <strong>Admin dashboard</strong> (see below).
        </p>
      </section>

      <section id="states-leagues-divisions" class="guide-section">
        <h2>States, leagues, and divisions</h2>
        <p>
          The app organizes competition by <strong>State</strong>,
          <strong>League</strong>, and <strong>Division</strong>. For example:
          <em>Illinois</em> (state) → <em>IESA</em> (league) → <em>A</em> or
          <em>AA</em> (division); or <em>Illinois</em> → <em>IKWF</em> →
          <em>Junior</em> / <em>Senior</em>.
        </p>
        <p>
          On the home screen, the <strong>Leagues</strong> area shows divisions
          grouped under their state and league. Each division (e.g. “IESA AA”)
          is a separate rankings view: when you tap a division card, you see
          Pound for Pound, weight-class rankings, and Rising Stars for that
          division only. Wrestlers are assigned to a league (and weight class)
          on their profile so they appear in the correct division’s rankings.
        </p>
      </section>

      <section id="wrestler-profile" class="guide-section">
        <h2>Wrestler profile</h2>
        <p>A wrestler’s profile shows:</p>
        <ul>
          <li>
            <strong>Photo</strong>, name, and
            <strong>record</strong> (wins–losses) for the current season.
          </li>
          <li>
            <strong>Skill</strong> number and a bar indicating skill level. A
            <strong>Skill trend</strong> sparkline shows how their rating
            changed over time based on matches.
          </li>
          <li>
            <strong>Rank in weight class</strong> — Shown below the skill trend
            when the wrestler has a league and weight set. You see “#X of Y”
            (e.g. #3 of 12) and, when available, a rank-change pill (e.g. ↑2 or
            ↓1). If league or weight is missing, set them on the profile (when
            logged in) to see rank.
          </li>
          <li><strong>Details</strong> — Grade, School, League, Weight.</li>
          <li>
            <strong>Profile change history</strong> — When you are logged in, an
            audit log of edits to the profile is shown. This section is hidden
            when you are not logged in.
          </li>
          <li>
            <strong>Match results</strong> — List of matches (newest first) with
            result (Win/Loss by type), date, location, and skill change. The
            list loads in pages; scroll down to load older bouts. Use the
            <strong>Season</strong> filter to focus one season (from recent
            match history). Tap a match to open the match detail. When logged
            in, an <strong>Add match</strong> button appears if there are no
            matches yet.
          </li>
        </ul>
        <p>
          <strong>Floating menu (when logged in):</strong> Home, Back,
          <strong>Add match</strong>, <strong>Edit wrestler</strong>,
          <strong>Change photo</strong>, <strong>Compare</strong>.
          <strong>Compare</strong> opens a search: type to find another wrestler
          by
          <strong
            >name, school, state, league, division, or weight class</strong
          >
          (results show profile photos). After you select someone, you see
          <strong>head-to-head</strong>, <strong>common opponents</strong>, and
          a simple <strong>match prediction</strong>. Wrestlers from different
          leagues or divisions can be compared.
        </p>
      </section>

      <section id="match-detail" class="guide-section">
        <h2>Match detail screen</h2>
        <p>
          Opening a match from the feed or from a wrestler’s match list shows
          the <strong>match detail</strong>: the two wrestlers, result (e.g. Win
          by Fall), date, location, type, and optional video link. If the match
          has a video URL, you can open it from this screen.
        </p>
        <p>
          <strong>Match change history</strong> — When you are logged in, an
          audit log of changes to the match is shown. This section is hidden
          when you are not logged in.
        </p>
        <p>
          <strong>Floating menu (when logged in):</strong> Home, Back,
          <strong>Edit match</strong>, <strong>Delete match</strong>, User
          Guide. <strong>Edit</strong> lets you change location, type, date,
          video, and season. <strong>Delete</strong>
          removes the match for both wrestlers and reverses their skill and
          win/loss changes; you are taken back to the wrestler profile after a
          successful delete.
        </p>
      </section>

      <section id="adding-match" class="guide-section">
        <h2>Adding a match</h2>
        <p>You can add a match in two ways (both require login):</p>
        <ul>
          <li>
            From a <strong>wrestler profile</strong> — Open the floating menu
            and choose <strong>Add match</strong>. One wrestler is fixed (the
            profile you’re on); you select the opponent, winner, match type,
            date, location, and optional video link.
          </li>
          <li>
            From <strong>Home</strong> — Open the floating menu and choose
            <strong>Add match</strong>. You choose both wrestlers, then winner,
            type, date, location, and optional video link.
          </li>
        </ul>
        <p>
          You must select both wrestlers, a winner, and a match type. Use
          <strong>Search</strong> next to each wrestler to open the search
          dialog (filter by name, school, state, weight, then pick a row).
          Opponents can be from <strong>any league or division</strong> —
          cross-league matches are allowed.
        </p>
        <p>
          The app checks that the same match (same two wrestlers, date, and
          type) isn’t already entered. After saving, both wrestlers’ skill and
          win/loss records are updated. Success and error messages appear as
          toasts at the bottom of the screen.
        </p>
      </section>

      <section id="adding-editing-wrestlers" class="guide-section">
        <h2>Adding and editing wrestlers</h2>
        <p>All of these require you to be logged in.</p>
        <p>
          <strong>Add wrestler</strong> — From the home floating menu. Enter
          first name, last name, school, grade, league, and weight class. New
          wrestlers start with skill 1200 and 0–0. After saving, a success toast
          is shown.
        </p>
        <p>
          <strong>Edit wrestler</strong> — From the wrestler profile floating
          menu. The dialog is pre-filled with the current data. You can change
          name, school, grade, league, weight class, and other fields. Changes
          are saved and reflected on the profile.
        </p>
        <p>
          <strong>Change photo</strong> — From the wrestler profile floating
          menu. Pick an image from your device; it is uploaded and set as the
          wrestler’s profile photo. A toast confirms success or shows an error.
        </p>
      </section>

      <section id="leagues-rankings" class="guide-section">
        <h2>Viewing league rankings</h2>
        <p>
          From the home screen, tap a <strong>division card</strong> (e.g. IESA
          AA) to open that division’s <strong>league rankings</strong> page. No
          login is required to view rankings.
        </p>
        <p>At the top of the rankings page you’ll see:</p>
        <ul>
          <li>
            <strong>Pound for Pound</strong> — Top wrestlers across all weights
            in that division, ordered by Skill. Each row shows rank, photo,
            name, and record; tap a row to open that wrestler’s profile.
          </li>
          <li>
            <strong>Weight classes</strong> — The division’s weight classes
            (e.g. 65 Pounds, 70 Pounds). For each weight you can expand to see
            the top 5 or tap <strong>See full rankings</strong> to open the full
            list for that weight.
          </li>
          <li>
            <strong>Rising Stars</strong> — Wrestlers with the largest skill
            gain over their last few matches. Each row shows rank, photo, name,
            record, and a sparkline of recent performance.
          </li>
        </ul>
        <p>
          On the <strong>full rankings</strong> page for a weight class, all
          wrestlers are listed by Skill (highest first), with record and school.
          <strong>Rank change badges</strong> (e.g. ↑3 or ↓2) show movement
          since the last time rankings were saved. Tap a row to open that
          wrestler’s profile.
        </p>
        <p>
          On a wrestler profile, <strong>rank in weight class</strong> (“#X of
          Y” and any ↑/↓ change) appears below the skill trend when the wrestler
          has a league and weight set.
        </p>
      </section>

      <section id="login-signup" class="guide-section">
        <h2>Login and sign up</h2>
        <p>
          <strong>Log in</strong> is available from the floating menu on the
          home screen (and from other screens that offer it). On the
          <strong>login</strong> page, use the link to
          <strong>create an account</strong> if you don’t have one yet. You need
          an account to add or edit wrestlers, add matches, edit or delete
          matches, and change photos.
        </p>
        <p>
          <strong>Browsing</strong> — Viewing wrestlers, rankings, the match
          feed, and division cards does not require login. Profile change
          history and match change history are only shown when you are logged
          in.
        </p>
      </section>

      <section id="dark-mode" class="guide-section">
        <h2>Dark mode</h2>
        <p>
          The app supports <strong>light and dark</strong> themes. Open the
          floating menu and choose <strong>Dark mode</strong> or
          <strong>Light mode</strong> to switch. Your choice is remembered on
          this device. If you haven’t chosen before, the app may follow your
          system’s light/dark preference.
        </p>
      </section>

      <section id="admin-tools" class="guide-section highlight">
        <h2>League management &amp; admin (operators)</h2>
        <p>
          A small number of <strong>designated operator accounts</strong> see
          two extra items in the home floating menu:
          <strong>Manage leagues</strong> and <strong>Admin dashboard</strong>.
          Most users will not see these.
        </p>
        <p>
          <strong>Manage leagues</strong> — Create and edit
          <strong>divisions</strong> (state, league name, division, display name
          for rankings), upload division logos, manage
          <strong>weight classes</strong> per division,
          <strong>import from IKWF-History.org CSV</strong> (teams, then
          wrestlers, then matches — only rows whose Division column matches that
          division; match import assigns each wrestler’s weight class from scale
          weight using that division’s weight-class list), and use admin tools
          such as the global skill recalculation when needed. This screen is for
          people who maintain the league structure in Rankster.
        </p>
        <p>
          <strong>Admin dashboard</strong> — Lists
          <strong>registered users</strong> (from sign-up / login). You can
          select a user and review <strong>audit logs</strong> of changes to
          wrestler profiles and matches attributed to that account. Use
          <strong>Refresh</strong> in the toolbar to reload users and logs.
        </p>
      </section>

      <section id="tips" class="guide-section">
        <h2>Tips for better data</h2>
        <ul>
          <li>
            Enter matches with the correct date and match type (Decision, Major,
            Fall, etc.) so skill updates are accurate.
          </li>
          <li>
            Set each wrestler’s league and weight class so they appear in the
            right division’s rankings and the “Rank in weight class” section on
            their profile shows the correct #X of Y.
          </li>
          <li>
            If you notice a duplicate or wrong match, use Edit or Delete on the
            match detail screen (when logged in) to fix it; the app will update
            both wrestlers’ records and skill.
          </li>
          <li>
            Success and error messages appear as <strong>toasts</strong> at the
            bottom of the screen; check there if you’re unsure whether an action
            succeeded.
          </li>
          <li>
            Use this User Guide anytime from the floating menu on any screen.
          </li>
        </ul>
      </section>
    </div>

    <div class="guide-footer">
      <v-btn color="primary" to="/">Back to Home</v-btn>
    </div>
    <FloatingNav />
  </div>
</template>

<script>
import FloatingNav from "../components/FloatingNav.vue";

export default {
  name: "UserGuide",
  components: { FloatingNav },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.user-guide-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.guide-header {
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.back-btn {
  color: #fff !important;
}

.guide-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
}

.guide-content {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
}

.guide-section {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.guide-section.highlight {
  border-left: 4px solid #c62828;
  background: #fff8f8;
}

.guide-toc .toc-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.guide-toc .toc-nav a {
  color: #c62828;
  text-decoration: none;
  font-weight: 500;
  padding: 0.25rem 0;
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s ease;
}

.guide-toc .toc-nav a:hover {
  border-bottom-color: #c62828;
}

.guide-section:target {
  scroll-margin-top: 0.5rem;
}

.guide-section h2 {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: #1f2937;
}

.guide-section p {
  margin: 0 0 0.5rem;
  line-height: 1.55;
  color: #374151;
}

.guide-section p:last-child {
  margin-bottom: 0;
}

.guide-section ul {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
}

.guide-section li {
  margin-bottom: 0.35rem;
  line-height: 1.5;
  color: #374151;
}

.guide-footer {
  max-width: 720px;
  margin: 0 auto;
  padding: 1rem;
  text-align: center;
}
</style>

<style>
/* User Guide: obey app light/dark mode */
.theme--dark .user-guide-page {
  background: #121212;
}

.theme--dark .guide-section {
  background: #1e1e1e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border-color: #374151;
}

.theme--dark .guide-section.highlight {
  border-left-color: #f87171;
  background: rgba(185, 28, 28, 0.12);
}

.theme--dark .guide-section h2 {
  color: #e5e7eb;
}

.theme--dark .guide-section p,
.theme--dark .guide-section li {
  color: #d1d5db;
}

.theme--dark .guide-toc .toc-nav a {
  color: #f87171;
}

.theme--dark .guide-toc .toc-nav a:hover {
  border-bottom-color: #f87171;
}

.theme--dark .guide-footer {
  border-top: 1px solid #374151;
}
</style>
